import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';

import { cn } from '@Utils/cn';

import FieldShell from './FieldShell';

interface RepeaterFieldProps {
  title?: string;
  description?: string;
  /** Item label prefix — "Button" renders fallback titles "Button 1", "Button 2"… */
  itemLabel?: string;
  items: Record<string, unknown>[];
  maxItems?: number;
  /**
   * Item boundaries come from background contrast, not borders: 'tinted'
   * (default) renders soft-gray cards for items sitting on the white panel;
   * 'contrast' renders white cards with a hairline border for repeaters
   * nested inside a tinted item (gray-in-gray would have no visible edge).
   */
  tone?: 'tinted' | 'contrast';
  /**
   * Authored identity for an item's header — e.g. a button's own label —
   * so rows are told apart at a glance. Falls back to "«itemLabel» «n»".
   */
  getItemTitle?: (
    item: Record<string, unknown>,
    index: number,
  ) => string | undefined;
  /** Renders one item's sub-form (SchemaFields supplies this from the item schema). */
  renderItem: (
    item: Record<string, unknown>,
    onItemChange: (next: Record<string, unknown>) => void,
    index: number,
  ) => React.ReactNode;
  /** Factory for a freshly added item (schema defaults). */
  createItem: () => Record<string, unknown>;
  onChange: (items: Record<string, unknown>[]) => void;
}

const headerButtonClass =
  'rounded-md p-1.5 text-zinc-400 hover:bg-zinc-500/10 hover:text-zinc-700 disabled:pointer-events-none disabled:opacity-30';

/** Add/remove/reorder list where each item is itself a small sub-form. */
export default function RepeaterField({
  title,
  description,
  itemLabel = 'Item',
  items,
  maxItems,
  tone = 'tinted',
  getItemTitle,
  renderItem,
  createItem,
  onChange,
}: RepeaterFieldProps) {
  const move = (from: number, to: number) => {
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  const atCapacity = maxItems !== undefined && items.length >= maxItems;

  return (
    <FieldShell title={title} description={description}>
      <div className="space-y-2">
        {items.map((item, index) => {
          const itemTitle = getItemTitle?.(item, index)?.trim();
          return (
            // Index keys are safe here: rows are positional and carry no identity.
            <div
              key={index}
              className={cn(
                'rounded-lg',
                tone === 'contrast'
                  ? 'border border-zinc-200 bg-white'
                  : 'bg-zinc-100/70',
              )}
            >
              <div className="flex items-center justify-between gap-2 py-1 pr-1 pl-3">
                <span
                  className={cn(
                    'truncate text-sm font-medium',
                    itemTitle ? 'text-zinc-800' : 'text-zinc-400',
                  )}
                >
                  {itemTitle ?? `${itemLabel} ${index + 1}`}
                </span>
                <div className="flex shrink-0 items-center">
                  <button
                    type="button"
                    aria-label="Move up"
                    disabled={index === 0}
                    onClick={() => move(index, index - 1)}
                    className={headerButtonClass}
                  >
                    <ChevronUp size={14} />
                  </button>
                  <button
                    type="button"
                    aria-label="Move down"
                    disabled={index === items.length - 1}
                    onClick={() => move(index, index + 1)}
                    className={headerButtonClass}
                  >
                    <ChevronDown size={14} />
                  </button>
                  <button
                    type="button"
                    aria-label={`Delete ${itemLabel} ${index + 1}`}
                    onClick={() =>
                      onChange(items.filter((_, i) => i !== index))
                    }
                    className={cn(headerButtonClass, 'hover:text-red-500')}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="space-y-3 px-3 pb-3">
                {renderItem(
                  item,
                  (next) =>
                    onChange(items.map((it, i) => (i === index ? next : it))),
                  index,
                )}
              </div>
            </div>
          );
        })}
        {!atCapacity && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full border-dashed text-zinc-600"
            onClick={() => onChange([...items, createItem()])}
          >
            <Plus size={14} />
            Add {itemLabel.toLowerCase()}
          </Button>
        )}
      </div>
    </FieldShell>
  );
}
